import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAccountCryptoBeneficiary } from "../../../redux/actions/CryptoTransaction/CryptoBeneficiary";
import InfoIcon from "../../icons/InfoIcon";
import { BeneficiaryDropdown, NetworkDropdown } from "../common/SelectDropdown";
import ErrorMessage from "../../../components/Common/ErrorMessage";
import { FormInput } from "../common/FormInput";
import { formatCryptoValue } from "../../utils/format";

const FieldLabel = ({ children, showInfo = false }) => (
  <label className="flex items-center gap-1.5 text-xs font-medium text-text-primary">
    {children}
    {showInfo && <InfoIcon />}
  </label>
);


const WithdrawEnterDetailsStep = ({
  selectedAsset,
  selectedBeneficiary,
  selectedNetwork,
  amount,
  onBeneficiaryChange,
  onNetworkChange,
  onAmountChange,
  onContinue,
  onAddBeneficiary,
  error,
  setError
}) => {
  const symbol = selectedAsset?.currency_code ?? "USDC";
  const parsedAmount = parseFloat(amount) || 0;
  const isFormValid = selectedBeneficiary && selectedNetwork && parsedAmount > 0 && parsedAmount <= selectedAsset?.balance;

  // TODO: Need to handle the max input.
  const handleAmountInput = (event) => {
    const next = event.target.value.replace(/[^0-9.]/g, "");
    const parts = next.split(".");
    if (parts.length > 2) return;
    onAmountChange?.(next);
  };

  const handleMaxClick = (balance = 0) => {
    onAmountChange?.(String(balance));
  };

  const [networks] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loadingNetworks] = useState(false);
  const [loadingBeneficiries, setLoadingBeneficiries] = useState(false);
  const [networkError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {    
    const getAccountBeneficiary = async () => {
      try {
        setLoadingBeneficiries(true);
        const beneficiaries = await dispatch(getAccountCryptoBeneficiary(selectedAsset.id));
        setBeneficiaries(beneficiaries);
        if (selectedBeneficiary) {
          const beneficiary = beneficiaries.find(beneficiary => beneficiary.id === selectedBeneficiary.id);
          handleOnChangeBeneficiary(beneficiary);
        }
      } catch (error) {
        console.error("Error fetching account beneficiaries:", error);
      } finally {
        setLoadingBeneficiries(false);
      }
    };

    if (selectedAsset?.id) {
      // getAccountNetworks();
      getAccountBeneficiary();
    }
    // eslint-disable-next-line
  }, [selectedAsset?.id]);


  const handleOnChangeBeneficiary = (beneficiary) => {
    onBeneficiaryChange(beneficiary);
    onNetworkChange({
      ...beneficiary?.network, 
      logo_url: beneficiary?.logo_url ?? beneficiary?.logo_url,
      asset_logo: beneficiary?.asset?.logo_url ?? null,
      asset_id: beneficiary?.asset?.asset_id ?? null
    });
  }

  const formattedBalance = formatCryptoValue(selectedAsset?.balance, selectedAsset?.decimals ?? 8);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 px-4 pt-4 custom-scrollbar overflow-y-scroll">
      {error && (
        <ErrorMessage message={error} onClose={() => setError(null)} />
      )}
      <h3 className="shrink-0 text-lg font-semibold leading-7 text-text-primary">
        Enter details
      </h3>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-visible">
        <BeneficiaryDropdown
          value={selectedBeneficiary}
          onChange={handleOnChangeBeneficiary}
          onAddBeneficiary={onAddBeneficiary}
          beneficiaries={beneficiaries}
          loading={loadingBeneficiries}
        />

        <NetworkDropdown
          value={selectedNetwork}
          onChange={onNetworkChange}
          networks={networks}
          loading={loadingNetworks}
          // disabled={selectedNetwork}
          error={networkError}
          disabled={true}
          // readOnly={true}
        />

        <div className="flex flex-col gap-2">
          <FieldLabel showInfo>Withdrawal Amount</FieldLabel>

          <div className="flex h-12 items-center gap-2 rounded-[10px] border border-divider pr-3">
            <FormInput
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountInput}
              placeholder="0.00"
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium text-text-primary outline-none focus:ring-0"
            />
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-sm font-medium text-text-primary">{symbol}</span>
              <button
                type="button"
                onClick={() => handleMaxClick(formattedBalance)}
                className="text-sm font-medium text-[#4ab932] custom-transition hover:text-[#3fa82c]"
              >
                Max
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-1 px-1 text-xs leading-[16.5px] text-text-secondary">
            <button type="button" className="custom-transition hover:text-text-secondary">
              Balance:
            </button>
            <span className="text-text-primary">
              {formattedBalance} {symbol}
            </span>
          </div>
        </div>

        <ul className="flex list-disc flex-col gap-2 pl-5 text-xs font-normal leading-[18px] text-text-secondary">
          <li>
            Do not withdraw directly to a crowdfund or ICO. We will not credit your account with tokens from that sale.
          </li>
          <li>
            Do not transact with Sanctioned Entities.{" "}
            {/* <button type="button" className="font-medium text-[#4ab932]">
              Learn more
            </button> */}
          </li>
        </ul>
      <button
        type="button"
        disabled={!isFormValid}
        onClick={onContinue}
        className="flex h-12 mb-2 w-full shrink-0 items-center justify-center rounded-[10px] bg-[#4ab933] text-sm font-semibold leading-[21px] text-white custom-transition hover:bg-[#3fa82c] disabled:cursor-not-allowed disabled:opacity-50"
      >
        Continue
      </button>
      </div>

    </div>
  );
};

export default WithdrawEnterDetailsStep;
